import React, { useEffect, useState } from "react";
import SectionTitleAndDescription from "./SectionTitleAndDescription";
import { getCompanyGroups, getIndustryGrpLeaderResult } from "api/search.api";
import { useSelector } from "react-redux";
import {
  addCandidateExcludeCompanyList,
  addCandidateWorkCompany,
  addCandidateWorkCompanyList,
  addExcludeCompany,
  removeCandidateWorkCompany,
  removeExcludeCompany,
  setEverWorked,
} from "store/slices/searcSlice";
import { RootState } from "store/store";
import { OptionType } from "components/Common/SearchableDropdown/SearchFilterDropdown";
import { useDebounce } from "utils";
import SelectIncludeCompany from "./SelectIncludeCompany";
import SelectExcludeCompanies from "./SelectExcludeCompanies";
import { Switch } from "antd";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";

interface CompanyGroup {
  id: number;
  title: string;
  companies_count: number;
  companies: string[];
  results: any[];
}

const SelectCompanies = () => {
  const [isIncludeCompany, setIsIncludeCompany] = useState(false);
  const [isExcludeCompany, setIsExcludeCompany] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [companiesGroup, setCompaniesGroup] = useState<CompanyGroup[]>([]); // Specify type here
  const [loading, setLoading] = useState(false);
  const everWorked = useAppSelector((state) => state.search.ever_worked);
  const dispatch = useAppDispatch();
  const onChange = (checked: boolean) => {
    dispatch(setEverWorked(checked ? true : false));
  };

  const [allIndustries, setAllIndustries] = useState<OptionType[]>([]);

  const includedCompanies = useSelector((state: RootState) => state.search.company);
  const excludedCompanies = useSelector((state: RootState) => state.search.exclude_company);

  const getGroups = async () => {
    try {
      setLoading(true);
      const res = await getCompanyGroups(1, page);

      setCompaniesGroup((prevCompaniesGroup) => {
        const newGroups = res.results.filter((newGroup: CompanyGroup) => {
          return !prevCompaniesGroup.some((existingGroup) => existingGroup.id === newGroup.id);
        });
        return [...prevCompaniesGroup, ...newGroups];
      });

      setTotalPages(res.total_pages);
      if (page < res.total_pages) {
        setPage(page + 1);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error(error.message);
      } else {
        console.error(String(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustryLeaders = async (query: string) => {
    try {
      const response = await getIndustryGrpLeaderResult(query);
      const data: any = response;
      const matches = data?.data?.autocompleteCompanyIndustry?.matches;
      setAllIndustries(
        matches?.map((item: any) => ({
          value: item.company,
          label: item.company,
        }))
      );
    } catch (err: any) {
      console.error(err);
    }
  };

  const debouncedSearchSkill = useDebounce((query: string) => {
    if (query) {
      fetchIndustryLeaders(query);
    }
  }, 500);

  useEffect(() => {
    if (companiesGroup.length === 0) {
      getGroups();
    }
  }, []);
  return (
    <>
      <div className="w-1/4">
        <SectionTitleAndDescription
          title="Select companies"
          isRequiredSection={false}
          isHelpInfoReq={false}
          description="Are there companies you want to target or exclude from your candidate search?"
        />
      </div>

      <div className="w-3/4">
        <SelectIncludeCompany
          title="Companies to include"
          dispatchAddAction={addCandidateWorkCompany}
          dispatchRemoveAction={removeCandidateWorkCompany}
          isIncludeCompany={isIncludeCompany}
          setIsIncludeCompany={setIsIncludeCompany}
          placeholder="Enter a company name, then select to include..."
          selectedCompanies={includedCompanies}
          dispatchGroupSelect={addCandidateWorkCompanyList}
        />
        <div className="w-fit ml-auto flex gap-4 justify-between mb-3">
          <SectionTitleAndDescription
            title="Ever Worked: "
            isRequiredSection={false}
            isHelpInfoReq={false}
          />
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            checked={everWorked}
            onChange={onChange}
          />
        </div>
        <SelectExcludeCompanies
          // title="Exclude Companies"
          title="Companies to exclude"
          dispatchAddAction={addExcludeCompany}
          dispatchRemoveAction={removeExcludeCompany}
          isIncludeCompany={isExcludeCompany}
          setIsIncludeCompany={setIsExcludeCompany}
          placeholder="Enter a company name, then select to exclude..."
          selectedCompanies={excludedCompanies}
          includedCompanies={includedCompanies}
          dispatchGroupSelect={addCandidateExcludeCompanyList}
        />
      </div>
    </>
  );
};

export default SelectCompanies;
